import React, { useState, useEffect } from 'react';
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
} from '@chakra-ui/react';
import SearchComponent from './SearchComponent';
import { fetchAgendaItemsForSession, fetchPartitionsAndSessions, fetchProgramItemsForSession } from './utils';
import { fetchUserAgenda } from '../../utils/api';
import PartitionCard from './PartitionCard';

const dateOptions = ['6/5/2024', '6/6/2024', '6/7/2024'];

const AgendaPage = () => {
  const [selectedTab, setSelectedTab] = useState(0); // 0 for Program, 1 for My Agenda
  const [selectedDateIndex, setSelectedDateIndex] = useState(0); // Index for dateOptions
  const [partitions, setPartitions] = useState([]);
  const [sessions, setSessions] = useState({});
  const [expandedIndex, setExpandedIndex] = useState([]); // State to track expanded Accordion items
  const [userAgenda, setUserAgenda] = useState([]); // State to track items in user's agenda

  useEffect(() => {
    const fetchData = async () => {
      const selectedDate = dateOptions[selectedDateIndex];
      const data = await fetchPartitionsAndSessions(selectedDate);
      const cleanedSessions = cleanSessionsData(data.sessions);
      const agenda = await fetchUserAgenda();
      setUserAgenda(agenda);
      setPartitions(data.partitions);
      setSessions(cleanedSessions);
      setExpandedIndex([]); // Reset expanded state when date changes
    };
    fetchData();
  }, [selectedDateIndex]);

  // New useEffect hook to update user's agenda when "My Agenda" tab is clicked
  useEffect(() => {
    const fetchAgenda = async () => {
      if (selectedTab === 1) {
        const agenda = await fetchUserAgenda();
        setUserAgenda(agenda);
      }
    };
    fetchAgenda();
  }, [selectedTab]);

  const cleanSessionsData = (sessionsData) => {
    const cleanedSessions = {};
    for (const partition in sessionsData) {
      cleanedSessions[partition] = sessionsData[partition].map((session) => ({
        ...session,
        time_start: session.time_start === 'NaN' ? '' : session.time_start,
        time_end: session.time_end === 'NaN' ? '' : session.time_end,
        session_title: session.session_title === 'NaN' ? '' : session.session_title,
        session_people: session.session_people === 'NaN' ? '' : session.session_people,
        session: session.session === 'NaN' ? '' : session.session,
        location: session.location === 'NaN' ? '' : session.location,
      }));
    }
    return cleanedSessions;
  };

  const handleShowItems = async (session) => {
    if (selectedTab === 0) {
      return await fetchProgramItemsForSession(session);
    } else {
      return await fetchAgendaItemsForSession(session, userAgenda);
    }
  };

  return (
    <div className="agenda-page">
      <Heading 
        as="h1" 
        size="xl" 
        className="page-title" 
        style={{
          background: 'linear-gradient(45deg, #E27A47 45%, #4BB19D 55%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Agenda
      </Heading>
      <SearchComponent userAgenda={userAgenda} />
      <Tabs isFitted variant="enclosed" index={selectedTab} onChange={(index) => setSelectedTab(index)}>
        <TabList className="tab-list">
          <Tab
            className={`tab ${selectedTab === 0 ? 'tab-selected' : ''}`}
            sx={{
              color: selectedTab === 0 ? '#E27A47' : '#00000',
              borderBottom: selectedTab === 0 ? '3px solid #2b6cb0' : 'none',
            }}
          >
            Program
          </Tab>
          <Tab
            className={`tab ${selectedTab === 1 ? 'tab-selected' : ''}`}
            sx={{
              color: selectedTab === 1 ? '#E27A47' : '#00000',
              borderBottom: selectedTab === 1 ? '3px solid #2b6cb0' : 'none',
            }}
          >
            My Agenda
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <DateTabs selectedDateIndex={selectedDateIndex} setSelectedDateIndex={setSelectedDateIndex} />
            <PartitionList partitions={partitions} sessions={sessions} onShowItems={handleShowItems} expandedIndex={expandedIndex} setExpandedIndex={setExpandedIndex} isAgendaTab={selectedTab}/>
          </TabPanel>
          <TabPanel>
            <DateTabs selectedDateIndex={selectedDateIndex} setSelectedDateIndex={setSelectedDateIndex} />
            <PartitionList partitions={partitions} sessions={sessions} onShowItems={handleShowItems} expandedIndex={expandedIndex} setExpandedIndex={setExpandedIndex} isAgendaTab={selectedTab}/>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

const DateTabs = ({ selectedDateIndex, setSelectedDateIndex }) => (
  <Tabs isFitted variant="enclosed" index={selectedDateIndex} onChange={(index) => setSelectedDateIndex(index)}>
    <TabList>
      <Tab>Jun 5</Tab>
      <Tab>Jun 6</Tab>
      <Tab>Jun 7</Tab>
    </TabList>
  </Tabs>
);

const PartitionList = ({ partitions, sessions, onShowItems, expandedIndex, setExpandedIndex, isAgendaTab }) => (
  <Accordion allowMultiple index={expandedIndex} onChange={setExpandedIndex}>
    {partitions.map((partition) => (
      <AccordionItem key={partition}>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            {partition}
          </Box>
        </AccordionButton>
        <AccordionPanel pb={4}>
          {sessions[partition].map((session, index) => (
            <PartitionCard
              key={session.id}
              item={session}
              index={index}
              items={sessions[partition]}
              onShowItems={onShowItems}
              isAgendaTab={isAgendaTab}
            />
          ))}
        </AccordionPanel>
      </AccordionItem>
    ))}
  </Accordion>
);

export default AgendaPage;
